import React, { useEffect, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { documentService } from '../../services/document.service'
import { Document } from '../../types'

interface MergeConfirmDialogProps {
  show: boolean
  onHide: () => void
  onConfirm: (metadataDocumentId: number, deleteOriginals: boolean, archiveFallback: boolean, documentIds: number[]) => void
  documentIds: number[]
}

export const MergeConfirmDialog: React.FC<MergeConfirmDialogProps> = ({ show, onHide, onConfirm, documentIds }) => {
  const { t } = useTranslation()
  const [documents, setDocuments] = useState<Document[]>([])
  const [orderedDocumentIds, setOrderedDocumentIds] = useState<number[]>([])
  const [metadataDocumentId, setMetadataDocumentId] = useState(-1)
  const [archiveFallback, setArchiveFallback] = useState(false)
  const [deleteOriginals, setDeleteOriginals] = useState(false)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)

  const loadDocuments = React.useCallback(async () => {
    try {
      const docs = await Promise.all(documentIds.map((id) => documentService.getDocument(id)))
      setDocuments(docs)
    } catch (error) {}
  }, [documentIds])

  useEffect(() => {
    if (show && documentIds.length > 0) {
      setOrderedDocumentIds([...documentIds])
      loadDocuments()
    }
  }, [show, documentIds, loadDocuments])

  const getDocument = (id: number): Document | undefined => {
    return documents.find((d) => d.id === id)
  }

  const handleDragStart = (index: number) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e: React.DragEvent, _index: number) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    if (draggedIndex === null) return

    const newOrder = [...orderedDocumentIds]
    const [draggedItem] = newOrder.splice(draggedIndex, 1)
    newOrder.splice(dropIndex, 0, draggedItem)

    setOrderedDocumentIds(newOrder)
    setDraggedIndex(null)
  }

  const handleConfirm = () => {
    onConfirm(metadataDocumentId, deleteOriginals, archiveFallback, orderedDocumentIds)
    // Reset state
    setMetadataDocumentId(-1)
    setArchiveFallback(false)
    setDeleteOriginals(false)
  }

  const handleCancel = () => {
    // Reset state
    setMetadataDocumentId(-1)
    setArchiveFallback(false)
    setDeleteOriginals(false)
    onHide()
  }

  return (
    <Modal show={show} onHide={handleCancel} backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{t('merge_confirm') || 'Potvrda spajanja'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          <strong>
            {t('merge_confirm_message', { count: documentIds.length }) ||
              `Ova operacija će spojiti ${documentIds.length} odabranih dokumenata u novi dokument.`}
          </strong>
        </p>

        <div className="form-group mb-3">
          <label className="form-label">{t('documents') || 'Dokumenti'}:</label>
          <ul className="list-group">
            {orderedDocumentIds.map((docId, index) => {
              const doc = getDocument(docId)
              return (
                <li
                  key={docId}
                  className="list-group-item"
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  style={{ cursor: 'move' }}
                >
                  <i className="bi bi-grip-vertical me-2"></i>
                  {doc?.title || `Document ${docId}`}
                </li>
              )
            })}
          </ul>
        </div>

        <div className="form-group mb-3">
          <label className="form-label" htmlFor="metadataDocumentSelect">
            {t('use_metadata_from') || 'Koristi metapodatke od'}:
          </label>
          <Form.Select id="metadataDocumentSelect" value={metadataDocumentId} onChange={(e) => setMetadataDocumentId(Number(e.target.value))}>
            <option value={-1}>{t('regenerate_all_metadata') || 'Regeneriši sve metapodatke'}</option>
            {documents.map((doc) => (
              <option key={doc.id} value={doc.id}>
                {doc.title}
              </option>
            ))}
          </Form.Select>
        </div>

        <Form.Check
          type="switch"
          id="archiveFallbackSwitch"
          label={t('merge_archive_fallback') || 'Pokušaj da uključiš arhivsku verziju u spajanje za ne-PDF fajlove'}
          checked={archiveFallback}
          onChange={(e) => setArchiveFallback(e.target.checked)}
          className="mb-2"
        />

        <Form.Check
          type="switch"
          id="deleteOriginalsSwitch"
          label={t('delete_originals_after_merge') || 'Obriši originalne dokumente nakon uspešnog spajanja'}
          checked={deleteOriginals}
          onChange={(e) => setDeleteOriginals(e.target.checked)}
        />

        {!archiveFallback && (
          <p className="small text-muted fst-italic mt-3">{t('merge_pdf_note') || 'Napomena: samo PDF fajlovi će biti uključeni.'}</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCancel}>
          {t('cancel') || 'Otkaži'}
        </Button>
        <Button variant="primary" onClick={handleConfirm}>
          {t('proceed') || 'Nastavi'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
